import type { ProjectWorkOrderDetailDto } from '@artic/api-client';

import type { WeekStartsOn } from '../capacity-plan/types';
import type { ContainerSelection } from '../products/types';

/**
 * Allowed shape of a single metadata value on `work_orders.metadata`. Mirrors
 * `WorkOrderMetadataValueSchema` in nest-backend's `project-planning.dto.ts` —
 * the server's coercion path always produces one of these primitives, and
 * cleared keys are stripped via jsonb_strip_nulls so values are never null.
 */
export type WorkOrderMetadataValue = string | number | boolean;

export const projectStatusValues = ['draft', 'released', 'redrafting'] as const;

export type ProjectStatus = (typeof projectStatusValues)[number];

/**
 * Project-level execution status, derived server-side from the project's
 * non-archived work orders (ENG-600 Timeline View). Distinct from
 * `project_status` (the draft ⇄ released planning lifecycle). Rules: no work
 * orders → draft; all complete → complete; any in_progress or partially
 * complete → in_progress; any scheduled/redrafting → scheduled; else draft.
 */
export const projectDerivedStatusValues = [
  'draft',
  'scheduled',
  'in_progress',
  'complete',
] as const;

export type ProjectDerivedStatus = (typeof projectDerivedStatusValues)[number];

export interface ProjectPlanningListItem {
  project_id: string;
  project_name: string;
  project_code: string;
  project_prefix: string | null;
  project_status: ProjectStatus;
  /** Work-order rollup driving the Timeline View status filters (ENG-600). */
  project_derived_status: ProjectDerivedStatus;
  is_archived: boolean;
  /** Distinct factory names referenced by the project's work orders (sorted alphabetically). */
  factory_names: string[];
  project_scheduled_start_date: string | null;
  project_scheduled_complete_date: string | null;
  /** Aggregated count of work orders on this project */
  work_order_count: number;
  /**
   * Work orders that block archiving the project: every one that is neither
   * draft nor archived — redrafting, scheduled, in progress or complete. The
   * project can be archived only while this is 0 (ENG-981).
   */
  archive_blocking_work_order_count: number;
  created_at: string;
  /** ISO timestamp the project was released (draft → released). Null while still draft, or for legacy releases predating the column. */
  released_at: string | null;
  /** Calendar date (YYYY-MM-DD) the project completed. Null until marked complete. */
  completed_at: string | null;
  /** ISO timestamp the project was archived. Null for active projects. */
  archived_at: string | null;
  /** Email of the project's creator (joined from auth.users). Null if the user was deleted or the project predates user tracking. */
  created_by_email: string | null;
  /** Profile name of the project's creator (joined from profiles.profile_name). Null when the user has no profile name. */
  created_by_name: string | null;
}

// ── Project Work Order Detail ────────────────────────────────────

export interface ProjectWorkOrderDetail {
  work_order_id: string;
  /**
   * Human-readable identifier (e.g. "BRK-001"). Auto-generated from the
   * project_prefix on creation; user-editable on draft projects via the
   * planning grid's "Work Order" column.
   */
  work_order_name: string;
  /**
   * Auto-generated 5-char alphanumeric PIN, used for Tusks barcode scans
   * and printed labels. Read-only after creation.
   */
  work_order_code: string;
  product_id: string;
  /**
   * Per-project creation-order sequence (1-based, assigned at creation).
   * Null for legacy / deploy-window rows created before the column existed.
   */
  work_order_project_sequence: number | null;
  /** Manufacturing Sequence Number (per-factory running order). */
  work_order_sequence_number: number | null;
  /** Install sequence number, independent of the manufacture sequence. */
  work_order_install_sequence_number: number | null;
  /**
   * Planned start timestamp in the factory timezone (ISO 8601). Null when
   * the work order has not been explicitly placed on the timeline.
   */
  work_order_scheduled_start: string | null;
  /** Planned completion timestamp in the factory timezone (ISO 8601). */
  work_order_scheduled_complete: string | null;
  /**
   * ENG-733: the planned start as at first release (ISO 8601) — the immutable
   * baseline slip is measured against. Read-only; captured server-side and
   * never written from the client. Null for work orders released before
   * ENG-733 shipped.
   */
  work_order_original_scheduled_start: string | null;
  /** ENG-733: the planned completion as at first release (ISO 8601). Read-only. */
  work_order_original_scheduled_complete: string | null;
  /** Calendar date (YYYY-MM-DD) when design was released for this work order. */
  work_order_design_release_date: string | null;
  /** Calendar date (YYYY-MM-DD) the work order is dispatched from the factory. */
  work_order_dispatch_date: string | null;
  /** Calendar date (YYYY-MM-DD) scheduled for the on-site install. */
  work_order_install_date: string | null;
  // ENG-520 Standard Columns. Numeric values are serialized as strings to
  // preserve precision. Unit is the canonical unit of the chosen system; the
  // editor converts other entry units (ft/yd/cm/m) before write.
  work_order_length_value: string | null;
  work_order_length_unit: 'mm' | 'cm' | 'm' | 'inch' | 'ft' | 'yd' | null;
  work_order_height_value: string | null;
  work_order_height_unit: 'mm' | 'cm' | 'm' | 'inch' | 'ft' | 'yd' | null;
  work_order_area_value: string | null;
  work_order_area_unit: 'm2' | 'sqft' | null;
  work_order_weight_value: string | null;
  work_order_weight_unit: 'kg' | 'lbs' | null;
  work_order_phase: string | null;
  work_order_zone: string | null;
  work_order_plot: string | null;
  work_order_level: string | null;
  work_order_truck: string | null;
  work_order_truck_load: string | null;
  product_key: string;
  /**
   * Product name. Seeded with the generated Product Code on creation, so it
   * tracks the container selections until a user renames the product via
   * the product details page.
   */
  product_name: string;
  /** Project UUID this work order is assigned to (null if unassigned). */
  project_id: string | null;
  /** Joined from projects.project_name; null if unassigned. */
  project_name: string | null;
  /** Factory UUID this work order is assigned to (null if unassigned). */
  factory_id: string | null;
  /** Joined from factories.factory_name; null if the row has no factory. */
  factory_name: string | null;
  /** ENG-665: route UUID this work order is assigned to (null if unassigned). */
  route_id: string | null;
  /** Joined from routes.route_name; null if the row has no route. */
  route_name: string | null;
  container_selections: ContainerSelection[];
  /**
   * Per-row metadata keyed by the partner's
   * `partner_work_order_metadata_definitions.metadata_key`. Value type
   * matches the field's declared metadata_type. Empty `{}` when no metadata
   * has been set.
   */
  metadata: Record<string, WorkOrderMetadataValue>;
  /**
   * Maintained by `work_orders_compute_current_status_trg` on the backend
   * from `is_archived` + start/complete dates + `bill_of_operations_id`.
   * Drives the planning grid's per-row editability gating.
   */
  work_order_current_status:
    | 'draft'
    | 'redrafting'
    | 'scheduled'
    | 'in_progress'
    | 'complete'
    | 'archived';

  // ── Progress (ENG-919) ───────────────────────────────────────────
  // Read-only Production Plan inputs, joined from `work_order_plan_states` and
  // the job-card completion weights by `GET /project-planning/:id`. Null (and
  // weighted % 0) for a work order without a plan state — draft, redrafting,
  // archived, or a factory that has never been recalculated.
  /** Weighted % complete from job-card completion weights; 0 with no job cards. */
  total_weighted_work_order_completion_percentage: number | null;
  /** Progress verdict for a Scheduled / In-Process work order; null when its cell is blank. */
  plan_status: ProjectWorkOrderDetailDto['plan_status'];
  /** Expected weighted % complete from elapsed open Shift Minutes; null when unmeasured. */
  expected_pct_complete: number | null;
  /** Planned Shift Minutes (H) — this work order's weight in a grouped Progress cell. */
  planned_shift_minutes: number | null;
  /** The scheduled-date rule behind a Behind verdict; null for percentage-only verdicts. */
  plan_date_rule: ProjectWorkOrderDetailDto['plan_date_rule'];
  /** Instant the persisted verdict was computed; null without a plan state. */
  plan_computed_at: string | null;
}

/**
 * Persisted AG Grid column state for the project planning grid. Mirrors
 * `ProjectColumnStateSchema` in nest-backend's project-planning.dto.ts —
 * array order encodes display order; only visibility, pinning, and width
 * round-trip through the API.
 */
export interface ProjectColumnState {
  colId: string;
  hide?: boolean;
  pinned?: 'left' | 'right' | null;
  width?: number;
}

export interface ProjectPlanningDetail {
  project_id: string;
  partner_id: string;
  project_name: string;
  project_code: string;
  project_prefix: string | null;
  project_status: ProjectStatus;
  /** Work-order rollup driving the Timeline View status filters (ENG-600). */
  project_derived_status: ProjectDerivedStatus;
  is_archived: boolean;
  project_scheduled_start_date: string | null;
  project_scheduled_complete_date: string | null;
  created_by: string | null;
  released_by: string | null;
  released_at: string | null;
  created_at: string;
  updated_at: string;
  work_orders: ProjectWorkOrderDetail[];
  /**
   * ENG-608: workload category state per PRODUCT referenced by the
   * non-archived work orders. The grid maps work order → product via
   * product_id; every row sharing a product shows identical state.
   */
  product_workload_categories: ProjectProductWorkload[];
  /**
   * Shared column layout for the project planning grid. Null when the
   * project has never been customised — fall back to column-builder defaults.
   */
  project_column_config: ProjectColumnState[] | null;
  /**
   * Optimistic-concurrency token for `project_column_config`. The client
   * sends this back on the next PATCH; if it doesn't match the row's current
   * value the server returns 409. Detached from `updated_at` so unrelated
   * project edits don't poison column-config saves.
   */
  project_column_config_updated_at: string;
  /**
   * The owning partner's first day of week as a date-fns index (0 = Sunday,
   * 1 = Monday), from `partners.partner_first_day_of_week`. Every project view
   * buckets and labels weeks with this rather than assuming Monday. Carried on
   * the detail because every project view already fetches it, so no extra
   * request is needed to get it.
   */
  week_starts_on: WeekStartsOn;
  /**
   * ENG-752: populated ONLY by the release endpoints (release / release-drafts).
   * Work orders that were just released with an empty Run Sheet — a Bill of
   * Operations whose released version has no operations, so zero job cards were
   * created and they cannot be started in Production Control. Absent on ordinary
   * reads. The planner is warned so their Run Sheet gets released.
   */
  released_work_orders_without_operations?: {
    work_order_id: string;
    work_order_name: string;
  }[];
}

// ── Grid row type (flat representation for AG Grid) ──────────────

// ── ENG-608 product workload categories ─────────────────────────────

/**
 * Derived confirmation state for a product's workload category in this
 * project. `reconfirm_required` = a container change rebound the work
 * orders to a different product; the previous value is retained for the
 * struck-through display.
 */
export type ProjectProductConfirmationState = 'suggested' | 'confirmed' | 'reconfirm_required';

export type ProjectWorkloadDataState = 'baseline' | 'estimated' | 'forecasted';

export interface ProjectProductWorkload {
  product_id: string;
  /** Product family code (joined option codes). */
  product_key: string;
  /** Planned total labour minutes over the released Bill of Operations version. */
  planned_total_minutes: number | null;
  product_type_code: string | null;
  /**
   * Category the picker highlights: the Estimated band of the planned
   * total when process categories are confirmed in the Bill of
   * Operations, else the Cat 3 Baseline placeholder.
   */
  suggested_category: number | null;
  /** Provenance of the suggestion (Forecasted reserved for ENG-554). */
  data_state: ProjectWorkloadDataState;
  confirmed_category: number | null;
  confirmed_at: string | null;
  confirmed_by: string | null;
  /** Carried-over value after a container change — shown struck through. */
  previous_confirmed_category: number | null;
  previous_confirmed_at: string | null;
  confirmation_state: ProjectProductConfirmationState;
}

export interface WorkOrderRow {
  work_order_id: string;
  product_id: string;
  /**
   * Position in the project's manufacturing sequence (1-based, unique per
   * project). Assigned in creation order and rewritten as a whole by Update
   * Project Manufacturing Sequence (ENG-926). Null for legacy / deploy-window
   * rows. Backs the grid's "Project Manufacturing Sequence" column.
   */
  work_order_project_sequence: number | null;
  /**
   * Human-readable identifier (e.g. "BRK-001"). Auto-generated on creation
   * from the project_prefix; user-editable on draft projects via the
   * planning grid's "Work Order" column.
   */
  work_order_name: string;
  /**
   * Auto-generated 5-char alphanumeric PIN. Read-only, hidden in the
   * planning grid; surfaced read-only in the partner Work Orders grid
   * as "Work Order PIN".
   */
  work_order_code: string;
  /** Project UUID the row is assigned to. Null if unassigned. */
  project_id: string | null;
  /** Joined project name for display; null when unassigned. */
  project_name: string | null;
  /** Factory UUID the row is assigned to. Null if unassigned. */
  factory_id: string | null;
  /** Joined factory name for display; null when unassigned. */
  factory_name: string | null;
  productCode: string;
  /**
   * Display name for the row's product. Mirrors the derived Product Code
   * while the product is un-renamed (product_name === product_key), so it
   * tracks container edits live; a custom name set on the product details
   * page is shown verbatim.
   */
  product_name: string;
  complexityScore: number;
  /** Manufacturing Sequence Number (per-factory running order). */
  work_order_sequence_number: number | null;
  /** Install sequence number, independent of the manufacture sequence. */
  work_order_install_sequence_number: number | null;
  /** Planned start timestamp ISO 8601; rendered as YYYY-MM-DD in the grid. */
  work_order_scheduled_start: string | null;
  /** Planned completion timestamp ISO 8601; rendered as YYYY-MM-DD in the grid. */
  work_order_scheduled_complete: string | null;
  /** ENG-733: planned start as at first release, ISO 8601. Read-only baseline. */
  work_order_original_scheduled_start: string | null;
  /** ENG-733: planned completion as at first release, ISO 8601. Read-only baseline. */
  work_order_original_scheduled_complete: string | null;
  /** Calendar date (YYYY-MM-DD) when design was released for this work order. */
  work_order_design_release_date: string | null;
  /** Calendar date (YYYY-MM-DD) the work order is dispatched from the factory. */
  work_order_dispatch_date: string | null;
  /** Calendar date (YYYY-MM-DD) scheduled for the on-site install. */
  work_order_install_date: string | null;
  // ENG-520 Standard Columns — flat on the row so AG Grid `field` references
  // resolve directly. Stored in canonical units (mm/inch/m2/sqft/kg/lbs);
  // editor converts other entry units before write.
  work_order_length_value: string | null;
  work_order_length_unit: 'mm' | 'cm' | 'm' | 'inch' | 'ft' | 'yd' | null;
  work_order_height_value: string | null;
  work_order_height_unit: 'mm' | 'cm' | 'm' | 'inch' | 'ft' | 'yd' | null;
  work_order_area_value: string | null;
  work_order_area_unit: 'm2' | 'sqft' | null;
  work_order_weight_value: string | null;
  work_order_weight_unit: 'kg' | 'lbs' | null;
  work_order_phase: string | null;
  work_order_zone: string | null;
  work_order_plot: string | null;
  work_order_level: string | null;
  work_order_truck: string | null;
  work_order_truck_load: string | null;
  /**
   * Per-row metadata blob, addressed by `metadata.<key>` AG Grid field paths.
   */
  metadata: Record<string, WorkOrderMetadataValue>;
  /** Mirrors `ProjectWorkOrderDetail.work_order_current_status`. */
  work_order_current_status:
    | 'draft'
    | 'redrafting'
    | 'scheduled'
    | 'in_progress'
    | 'complete'
    | 'archived';
  // ── ENG-919 Progress (leaf badge + live-detail card; read-only) ──
  // Only the scalars `PlanStatusCellRenderer` reads. `plan_date_rule` stays
  // off the row on purpose: it is an object, and `workOrderRowShallowEqual`
  // compares everything but `metadata` by reference, so a server round-trip
  // would re-mint every row carrying one. `planned_shift_minutes` is a
  // grouping weight, and this grid never groups.
  plan_status: ProjectWorkOrderDetailDto['plan_status'];
  expected_pct_complete: number | null;
  plan_computed_at: string | null;
  total_weighted_work_order_completion_percentage: number | null;
  /** Dynamic container_key → option_code mappings */
  [containerKey: string]: string | number | null | Record<string, WorkOrderMetadataValue>;
}

// ── Batch update types ───────────────────────────────────────────

export interface CellUpdate {
  work_order_id: string;
  field: string;
  value: string | null;
}

/** Shape sent to the batch PATCH API */
export type BatchUpdateCell = CellUpdate;

/** Shape stored in the debounce buffer */
export type PendingUpdate = CellUpdate;
